import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type BlockType = 'h1' | 'h2' | 'h3' | 'p' | 'quote' | 'bullet' | 'numbered' | 'todo' | 'divider' | 'code';

export interface Block {
  id: string;
  type: BlockType;
  text: string;
  checked?: boolean; // for todo
}

export interface Doc {
  id: string;
  name: string;
  icon: string;
  color: string;
  emoji: string;
  blocks: Block[];
  createdAt: string;
  updatedAt: string;
}

const uid = (p = 'id') => `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const now = () => new Date().toISOString();

function makeSeed(): Doc[] {
  return [{
    id: 'doc_welcome',
    name: 'Welcome to MyDay Docs',
    icon: '',
    emoji: '',
    color: 'from-neon-blue to-neon-cyan',
    createdAt: now(),
    updatedAt: now(),
    blocks: [
      { id: uid('b'), type: 'h1', text: 'Welcome to MyDay Docs' },
      { id: uid('b'), type: 'p', text: 'Lightweight, block-based pages for everything that doesn\'t belong on a board — specs, meeting notes, standups, policies.' },
      { id: uid('b'), type: 'h2', text: 'How it works' },
      { id: uid('b'), type: 'p', text: 'Type text. Press "/" at the start of a line to change its type (heading, quote, checklist, etc.). Press Enter to add a new block.' },
      { id: uid('b'), type: 'bullet', text: 'Headings organize sections' },
      { id: uid('b'), type: 'bullet', text: 'Todos track action items' },
      { id: uid('b'), type: 'bullet', text: 'Quotes highlight the good stuff' },
      { id: uid('b'), type: 'quote', text: 'Work should feel lighter than it usually does.' },
      { id: uid('b'), type: 'h2', text: 'Try it' },
      { id: uid('b'), type: 'todo', text: 'Write your first doc', checked: false },
      { id: uid('b'), type: 'todo', text: 'Share it with a teammate', checked: false },
      { id: uid('b'), type: 'todo', text: 'Link it from a board', checked: false },
    ],
  }];
}

interface DocsState {
  docs: Doc[];
  createDoc: (name: string) => string;
  renameDoc: (id: string, name: string) => void;
  setIcon: (id: string, icon: string) => void;
  deleteDoc: (id: string) => void;
  setBlocks: (id: string, blocks: Block[]) => void;
  resetToSeed: () => void;
}

export const useDocsStore = create<DocsState>()(
  persist(
    (set) => ({
      docs: makeSeed(),
      createDoc: (name) => {
        const id = uid('doc');
        set((s) => ({
          docs: [...s.docs, {
            id, name: name || 'Untitled',
            icon: '', emoji: '',
            color: 'from-neon-purple to-neon-blue',
            blocks: [{ id: uid('b'), type: 'h1', text: name || 'Untitled' }],
            createdAt: now(), updatedAt: now(),
          }],
        }));
        return id;
      },
      renameDoc: (id, name) => set((s) => ({
        docs: s.docs.map((d) => d.id === id ? { ...d, name, updatedAt: now() } : d),
      })),
      setIcon: (id, icon) => set((s) => ({
        docs: s.docs.map((d) => d.id === id ? { ...d, icon, emoji: icon, updatedAt: now() } : d),
      })),
      deleteDoc: (id) => set((s) => ({
        docs: s.docs.filter((d) => d.id !== id),
      })),
      setBlocks: (id, blocks) => set((s) => ({
        docs: s.docs.map((d) => d.id === id ? { ...d, blocks, updatedAt: now() } : d),
      })),
      resetToSeed: () => set({ docs: makeSeed() }),
    }),
    { name: 'myday-docs', version: 1 },
  ),
);

export const newBlock = (type: BlockType = 'p', text = ''): Block => ({
  id: uid('b'), type, text,
});
